// Which slide the clicker shows, reconciled against the server.
//
// The clicker shows the slide the server says the room is on, and nothing
// else. It used to step on tap, before the server answered, so a tap felt
// instant. But a tap is a key press on the deck, and on a slide with click
// animations it plays the next animation and stays put (COP-92). Counting it
// as a slide ran the clicker ahead of the deck until it showed "End of deck"
// halfway through. Only the deck knows whether a tap changed slides, so with a
// deck connected the server leaves the slide alone on a tap and the deck's own
// report moves it.
//
// Versions keep it from going backwards. Every slide the server sends carries
// `slideSeq` (bumped on every change) and `slideEpoch` (this server copy of the
// session). A frame older than one already applied is ignored — a snapshot
// built before a change but delivered after it (a watchdog JOIN reply, the
// DETAILS_UPDATED a START broadcasts) used to put the phone a slide behind the
// deck until its next re-join. A new epoch — the backend restarted and rebuilt
// the session — starts the count over.
//
// A frame without a version comes from a backend that predates this; it is
// applied as before (absolute).

export interface SlideSyncState {
  /** What the clicker shows: the newest slide the server has told us. */
  shown: number;
  /** Its version. */
  seq: number;
  epoch: string | null;
}

/** Slide-carrying frame from the server (UPDATE_SLIDE or DETAILS_UPDATED). */
export interface ServerSlide {
  slide: number;
  seq?: number | null;
  epoch?: string | null;
}

export const initialSlideSync: SlideSyncState = {
  shown: 0,
  seq: -1,
  epoch: null,
};

/** A slide from the server. Returns the state unchanged for a stale frame. */
export function receive(state: SlideSyncState, frame: ServerSlide): SlideSyncState {
  const versioned =
    typeof frame.seq === "number" && typeof frame.epoch === "string" && frame.epoch !== "";
  if (!versioned) {
    // Legacy backend: absolute, as before.
    return { shown: frame.slide, seq: -1, epoch: null };
  }

  const stale = frame.epoch === state.epoch && (frame.seq as number) < state.seq;
  if (stale) return state;

  return { shown: frame.slide, seq: frame.seq as number, epoch: frame.epoch as string };
}
